/*
 * File: OrdersPage.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the page that shows the list of orders
 */
import React, { useState, useEffect, useContext } from "react";
import OrdersPageToolbar from "../components/OrdersPageToolbar";
import HandleOrders from "../components/HandleOrders";
import RenderOrders from "../components/RenderOrders";
import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import OrdersFilterTabs from "../components/OrdersFilterTabs";
import UIFab from "components/ui/UIFab";
import PropTypes from "prop-types";
import OrderForm from "../components/OrderForm";
import UIFormDialog from "components/ui/UIFormDialog";
import OrderFiltersDialog from "../components/OrderFiltersDialog";
import OrderModel from "../store/models/Order";
import UIProgress from "components/ui/UIProgress";
import UIStore from "../store/UI";
import { StoreContext, useStore } from "utils/useStore";
import { observer } from "mobx-react";

const OrdersPage = ({ appStore }) => {
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const uiStore = useStore(UIStore);
  const storeManager = useContext(StoreContext);

  useEffect(() => {
    appStore.setTitle("Orders");
    appStore.setRightNav();
  }, [appStore]);

  const callBack = () => {
    //Causes a refresh if an order is submitted
    setLoading(true);
    toggleFormDialog();
    setLoading(false);
  };

  const toggleFormDialog = () => {
    setShowFormDialog(!showFormDialog);
  };

  const newOrderForm = () => {
    uiStore.togglePatientSelection();
    toggleFormDialog();
  };

  const toggleFilterDialog = () => {
    setShowFilterDialog(!showFilterDialog);
  };

  const orderForm = () => {
    let newOrder = OrderModel.create(undefined, storeManager.newEnv());
    return <OrderForm order={newOrder} parentCallBack={callBack} />;
  };

  if (loading) return <UIProgress />;

  return (
    <HandleOrders
      render={(orders, actions) => (
        <React.Fragment>
          <UIFormDialog
            open={showFormDialog}
            toggleDialog={toggleFormDialog}
            title={"New Order"}
            viewPage={orderForm}
          />
          <OrderFiltersDialog
            showFilterDialog={showFilterDialog}
            toggleFilterDialog={toggleFilterDialog}
          />
          <OrdersPageToolbar toggleFilterDialog={toggleFilterDialog} />
          {orders.fetched && (
            <OrdersFilterTabs
              tabValue={orders.tabValue}
              handleTabChange={actions.handleTabChange}
              userCount={orders.usersOrdersCount}
              allCount={orders.ordersCount}
            />
          )}
          <RenderOrders
            orders={orders.orders}
            ordersFetched={orders.fetched}
            hasNextPage={orders.hasNextPage}
            isNextPageLoading={orders.isNextPageLoading}
            loadNextPage={actions.loadNextPage}
          />
          <UIFab
            icon={<AssignmentTurnedIn />}
            name={"New Order"}
            handleClick={newOrderForm}
          />
        </React.Fragment>
      )}
    />
  );
};

OrdersPage.propTypes = {
  appStore: PropTypes.object.isRequired
};

export default observer(OrdersPage);
